import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Headphones, Calendar, Clock, ExternalLink, Plus, Info } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SocialFollowButtons from '../components/SocialFollowButtons';
import ScriptureHeader from '../components/ScriptureHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ministry Banner
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

// Spotify Configuration - UPDATE THESE WITH YOUR ACTUAL PODCAST INFO
const SPOTIFY_CONFIG = {
  showUrl: 'https://open.spotify.com/show/0uRoGxumk0eCNdLs0TJCDP',
  showId: '0uRoGxumk0eCNdLs0TJCDP',
  showName: 'Living Fruit Ministry Podcast',
  // Other platforms (optional)
  applePodcastUrl: '',
  googlePodcastUrl: '',
};

export default function PodcastPage() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [newEpisode, setNewEpisode] = useState({
    title: '',
    description: '',
    spotify_url: '',
    date: '',
    duration: ''
  });

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      const response = await axios.get(`${API}/podcasts`);
      setEpisodes(response.data);
    } catch (error) {
      console.error('Error fetching episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEpisode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/podcasts`, newEpisode);
      toast.success('Episode added successfully!');
      setShowAddForm(false);
      setNewEpisode({ title: '', description: '', spotify_url: '', date: '', duration: '' });
      fetchEpisodes();
    } catch (error) {
      toast.error('Failed to add episode');
    }
  };

  const getSpotifyEmbedUrl = (url) => {
    if (!url) return '';
    
    // Convert Spotify URL to embed URL
    // https://open.spotify.com/episode/xxx -> https://open.spotify.com/embed/episode/xxx
    // https://open.spotify.com/show/xxx -> https://open.spotify.com/embed/show/xxx
    if (url.includes('spotify.com') && !url.includes('/embed/')) {
      return url.replace('spotify.com/', 'spotify.com/embed/');
    }
    return url;
  };

  // Placeholder episodes
  const displayEpisodes = episodes.length > 0 ? episodes : [
    { id: '1', title: 'Walking in Faith Daily', date: '2024-12-01', duration: '32:15', description: 'Practical steps for living out your faith in everyday situations.', spotify_url: '' },
    { id: '2', title: 'The Power of Community', date: '2024-11-24', duration: '28:45', description: 'Why fellowship matters and how to build meaningful connections.', spotify_url: '' },
    { id: '3', title: 'Serving with Purpose', date: '2024-11-17', duration: '35:20', description: 'Discovering your calling and using your gifts for God\'s kingdom.', spotify_url: '' },
  ];

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="podcast-page">
      {/* Scripture Header */}
      <ScriptureHeader page="podcast" />

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">Listen Anywhere</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Podcast</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto mb-8">
              Take our ministry with you wherever you go. Available on Spotify and all major platforms.
            </p>
            
            {/* Platform Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
              {/* Spotify Follow Button */}
              {SPOTIFY_CONFIG.showUrl ? (
                <a
                  href={SPOTIFY_CONFIG.showUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1DB954] text-white px-6 py-3 rounded-full hover:bg-[#1ed760] transition-colors font-medium"
                  data-testid="spotify-follow"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Follow on Spotify
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 bg-[#1DB954] text-white px-6 py-3 rounded-full opacity-80">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Follow on Spotify
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Coming Soon</span>
                </div>
              )}

              {/* Apple Podcasts */}
              {SPOTIFY_CONFIG.applePodcastUrl && (
                <a
                  href={SPOTIFY_CONFIG.applePodcastUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#9933FF] to-[#FF3399] text-white px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-medium"
                  data-testid="apple-podcasts"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M12.001 2C6.478 2 2 6.477 2 12c0 4.952 3.657 9.043 8.403 9.795v-3.768c-.638-.16-1.208-.427-1.666-.774-.636-.48-1.084-1.115-1.317-1.864-.167-.537-.135-1.124.091-1.606.224-.476.62-.858 1.114-1.093.294-.14.612-.213.935-.229.318-.016.64.024.95.116.635.189 1.2.57 1.627 1.094l.002.002v-5.47c2.476.455 4.366 2.63 4.366 5.257 0 1.19-.392 2.288-1.054 3.176l2.155 1.23c.932-1.271 1.486-2.834 1.486-4.524C19.091 7.14 16.005 4 12.001 4c-4.004 0-7.09 3.14-7.09 7 0 3.86 3.086 7 7.09 7 .41 0 .816-.034 1.212-.101V22c-.4.066-.81.1-1.212.1C6.478 22.1 2 17.622 2 12.1 2 6.578 6.478 2.1 12.001 2.1c5.523 0 10.001 4.478 10.001 10 0 2.31-.79 4.44-2.107 6.134l-2.178-1.248c1.004-1.262 1.606-2.859 1.606-4.587 0-4.144-3.363-7.5-7.508-7.5z"/>
                  </svg>
                  Apple Podcasts
                </a>
              )}
            </div>

            <button
              onClick={() => setShowSetupGuide(true)}
              className="inline-flex items-center gap-2 text-charcoal/60 hover:text-charcoal transition-colors text-sm"
            >
              <Info size={16} />
              Setup Guide
            </button>
          </motion.div>

          {/* Spotify Show Embed */}
          {SPOTIFY_CONFIG.showId ? (
            <div className="mb-12 bg-white rounded-2xl p-6 border border-gold/10">
              <h2 className="font-serif text-xl text-charcoal mb-4 flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-[#1DB954]">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                {SPOTIFY_CONFIG.showName}
              </h2>
              <iframe
                src={`https://open.spotify.com/embed/show/${SPOTIFY_CONFIG.showId}?theme=0`}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-xl"
                title={SPOTIFY_CONFIG.showName}
              />
            </div>
          ) : (
            <div className="mb-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 text-center">
              <svg viewBox="0 0 24 24" className="w-12 h-12 fill-[#1DB954] mx-auto mb-4">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <h2 className="font-serif text-xl text-charcoal mb-2">Podcast Coming Soon</h2>
              <p className="text-charcoal/60 text-sm max-w-md mx-auto mb-4">
                We're setting up our Spotify podcast to bring you audio content. Follow us to be notified when we launch!
              </p>
              <button
                onClick={() => setShowSetupGuide(true)}
                className="text-[#1DB954] hover:underline text-sm font-medium"
              >
                Staff: View Setup Instructions →
              </button>
            </div>
          )}

          {/* Social Follow Section */}
          <div className="mb-12 bg-white rounded-2xl p-8 border border-gold/10">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl text-charcoal mb-2">Follow & Subscribe</h2>
              <p className="text-charcoal/60">Stay connected with Living Fruit Ministry on all platforms</p>
            </div>
            <div className="flex justify-center">
              <SocialFollowButtons 
                variant="full"
                platforms={['spotify', 'youtube', 'facebook', 'instagram', 'tiktok', 'twitter']}
                showTitle={false}
                className="justify-center"
              />
            </div>
          </div>

          {/* Add Episode Button */}
          <div className="flex justify-end mb-8">
            <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
              <DialogTrigger asChild>
                <Button className="bg-burgundy hover:bg-purple text-white" data-testid="add-episode-btn">
                  <Plus size={18} className="mr-2" /> Add Episode
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Add Podcast Episode</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddEpisode} className="space-y-4 mt-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={newEpisode.title}
                      onChange={(e) => setNewEpisode({...newEpisode, title: e.target.value})}
                      required
                      data-testid="episode-title-input"
                    />
                  </div>
                  <div>
                    <Label>Spotify Episode URL</Label>
                    <Input
                      value={newEpisode.spotify_url}
                      onChange={(e) => setNewEpisode({...newEpisode, spotify_url: e.target.value})}
                      placeholder="https://open.spotify.com/episode/..."
                      required
                      data-testid="episode-spotify-input"
                    />
                    <p className="text-xs text-charcoal/50 mt-1">
                      Paste your Spotify episode link
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newEpisode.date}
                        onChange={(e) => setNewEpisode({...newEpisode, date: e.target.value})}
                        required
                        data-testid="episode-date-input"
                      />
                    </div>
                    <div>
                      <Label>Duration</Label>
                      <Input
                        value={newEpisode.duration}
                        onChange={(e) => setNewEpisode({...newEpisode, duration: e.target.value})}
                        placeholder="32:15"
                        data-testid="episode-duration-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newEpisode.description}
                      onChange={(e) => setNewEpisode({...newEpisode, description: e.target.value})}
                      rows={3}
                      data-testid="episode-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-burgundy hover:bg-purple" data-testid="submit-episode-btn">
                    Add Episode
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Episodes List */}
          <div className="space-y-4">
            {displayEpisodes.map((episode, i) => (
              <motion.div
                key={episode.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gold/10 hover:border-gold/30 transition-all duration-300"
                data-testid={`episode-card-${i}`}
              >
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Icon */}
                  <div className="w-20 h-20 bg-[#1DB954]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Headphones size={32} className="text-[#1DB954]" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-serif text-xl text-charcoal mb-2">{episode.title}</h3>
                    <p className="text-charcoal/60 text-sm mb-4">{episode.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-xs text-charcoal/50">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {episode.date}
                      </span>
                      {episode.duration && (
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {episode.duration}
                        </span>
                      )}
                      {episode.spotify_url && (
                        <a
                          href={episode.spotify_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#1DB954] hover:underline font-medium"
                        >
                          <ExternalLink size={14} /> Listen on Spotify
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Spotify Embed */}
                {episode.spotify_url && episode.spotify_url.includes('spotify.com') && (
                  <div className="mt-4">
                    <iframe
                      src={getSpotifyEmbedUrl(episode.spotify_url)}
                      width="100%"
                      height="152"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="rounded-xl"
                      title={episode.title}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          
          {displayEpisodes.length === 0 && !loading && (
            <div className="text-center py-12">
              <Headphones size={48} className="mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50">No episodes yet. Add your first episode!</p>
            </div>
          )}
        </div>
      </section>

      {/* Setup Guide Modal */}
      <Dialog open={showSetupGuide} onOpenChange={setShowSetupGuide}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-[#1DB954]">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              Spotify Podcast Setup Guide
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Step 1 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 1: Create a Spotify for Podcasters Account</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Go to <a href="https://podcasters.spotify.com" target="_blank" rel="noopener noreferrer" className="text-[#1DB954] hover:underline">podcasters.spotify.com</a></li>
                <li>Click "Get Started"</li>
                <li>Sign up with your Spotify account or create a new one</li>
                <li>Complete your podcast profile setup</li>
              </ol>
            </div>

            {/* Step 2 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 2: Create Your Podcast</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Click "Create a Podcast"</li>
                <li>Enter your podcast name: "Living Fruit Ministry Podcast"</li>
                <li>Add a description about your ministry</li>
                <li>Upload podcast cover art (1400x1400 pixels recommended)</li>
                <li>Select category: "Religion & Spirituality"</li>
              </ol>
            </div>

            {/* Step 3 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 3: Upload an Episode</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Click "New Episode"</li>
                <li>Upload your audio file (MP3 recommended)</li>
                <li>Add episode title and description</li>
                <li>Set publish date</li>
                <li>Click "Publish"</li>
              </ol>
            </div>

            {/* Step 4 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 4: Get Your Podcast URL</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>After your podcast is published, go to <a href="https://open.spotify.com" target="_blank" rel="noopener noreferrer" className="text-[#1DB954] hover:underline">open.spotify.com</a></li>
                <li>Search for your podcast name</li>
                <li>Click on your podcast</li>
                <li>Copy the URL from your browser (e.g., https://open.spotify.com/show/xxxxx)</li>
              </ol>
            </div>

            {/* Step 5 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 5: Add Episode to Website</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Come back to this Podcast page</li>
                <li>Click "Add Episode"</li>
                <li>Paste your Spotify episode URL</li>
                <li>Fill in the title, date, and description</li>
                <li>Click "Add Episode" to publish</li>
              </ol>
            </div>

            {/* Example URLs */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Supported Spotify URL Formats:</h3>
              <ul className="text-sm text-charcoal/70 space-y-1 font-mono">
                <li>• https://open.spotify.com/show/SHOW_ID</li>
                <li>• https://open.spotify.com/episode/EPISODE_ID</li>
              </ul>
            </div>

            {/* Recording Tips */}
            <div className="bg-amber-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">💡 Recording Tips</h3>
              <ul className="text-sm text-charcoal/70 space-y-1">
                <li>• Use a quiet room with minimal echo</li>
                <li>• A USB microphone greatly improves audio quality</li>
                <li>• Record in MP3 format (128-320 kbps)</li>
                <li>• Keep episodes 20-45 minutes for best engagement</li>
                <li>• Add intro/outro music for professional feel</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
